import React, { useState } from 'react';
import {
  Box, Collapse, FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch, TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, DeleteOutline, LockOutlined, TuneOutlined } from '@mui/icons-material';
import type { AdmissionField, AdmissionFieldType } from '../../../../types/admissions';
import { ToneChip } from '../../../common/StatusChip';
import FieldOptionsInput from '../../form/FieldOptionsInput';
import { fieldTypeOptionsFor, isRequiredSystemFieldKey, takesOwnOptions } from '../../form/admissionFormFields';
import type { DraftField } from './useFormDraft';

interface Props {
  field: DraftField;
  /** Applicants have answered it: it stays on the form and keeps its type. */
  answered: boolean;
  first: boolean;
  last: boolean;
  autoFocus?: boolean;
  onChange: (fieldId: string, patch: Partial<AdmissionField>) => void;
  onMove: (fieldId: string, by: -1 | 1) => void;
  onRemove: (fieldId: string) => void;
}

const num = (v: string) => (v === '' ? undefined : Number(v));

/**
 * One question on the form: its wording, type and whether it's required,
 * with the rest (help text, placeholder, limits, privacy) tucked under
 * "More". Built-in fields keep EduBook's wording and type; the ones every
 * application needs can't be made optional or removed.
 */
const FieldCard: React.FC<Props> = ({ field, answered, first, last, autoFocus, onChange, onMove, onRemove }) => {
  const [more, setMore] = useState(false);
  const alwaysAsked = isRequiredSystemFieldKey(field.key);
  const builtIn = field.isSystem;
  const hidden = field.enabled === false;
  const set = (patch: Partial<AdmissionField>) => onChange(field.uid, patch);
  const limits = field.type === 'number' ? (['min', 'max'] as const)
    : field.type === 'text' || field.type === 'textarea' ? (['minLength', 'maxLength'] as const) : null;

  const removeHint = alwaysAsked ? 'Every application needs this, so it stays on the form.'
    : answered ? 'Families have answered this, so it stays. You can hide it instead, under More.'
      : builtIn ? 'Remove. You can add it back from Add question.' : 'Remove';

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        borderRadius: 2.5, overflow: 'hidden', transition: 'border-color 150ms',
        opacity: hidden ? 0.6 : 1,
        '&:hover, &:focus-within': { borderColor: alpha(t.palette.primary.main, 0.4) },
      })}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
          {builtIn ? (
            // EduBook's own wording and type: shown, not edited.
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flex: 1, minWidth: 0, minHeight: 40 }}>
              <Tooltip title="Built-in: its wording and type are EduBook’s.">
                <LockOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
              </Tooltip>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>{field.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ width: { md: 170 }, flexShrink: 0, pl: { md: 1.75 } }}>
                {fieldTypeOptionsFor(field).find(t => t.value === field.type)?.label}
              </Typography>
            </Stack>
          ) : (
            <>
              <TextField
                value={field.label}
                onChange={e => set({ label: e.target.value })}
                placeholder="Question"
                size="small"
                fullWidth
                autoFocus={autoFocus}
                sx={{ flex: 1 }}
              />
              <Tooltip title={answered ? 'Families have answered this, so its type stays.' : ''}>
                <TextField
                  select
                  value={field.type}
                  onChange={e => set({ type: e.target.value as AdmissionFieldType, options: takesOwnOptions({ key: field.key, type: e.target.value as AdmissionFieldType }) ? field.options ?? [] : undefined })}
                  size="small"
                  disabled={answered}
                  sx={{ width: { md: 170 }, flexShrink: 0 }}
                >
                  {fieldTypeOptionsFor(field).map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
              </Tooltip>
            </>
          )}
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0, height: 40 }}>
            <Tooltip title={alwaysAsked ? 'Every application needs this.' : ''}>
              <FormControlLabel
                control={<Switch size="small" checked={field.required} disabled={alwaysAsked} onChange={(_, v) => set({ required: v })} />}
                label={<Typography variant="body2" color="text.secondary">Required</Typography>}
                sx={{ mr: 0.5, ml: 0 }}
              />
            </Tooltip>
            <Tooltip title="More settings">
              <IconButton size="small" onClick={() => setMore(v => !v)} color={more ? 'primary' : 'default'} aria-expanded={more}>
                <TuneOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" disabled={first} onClick={() => onMove(field.uid, -1)} aria-label="Move up"><ArrowUpward fontSize="small" /></IconButton>
            <IconButton size="small" disabled={last} onClick={() => onMove(field.uid, 1)} aria-label="Move down"><ArrowDownward fontSize="small" /></IconButton>
            <Tooltip title={removeHint}>
              <span>
                <IconButton size="small" disabled={alwaysAsked || answered} onClick={() => onRemove(field.uid)} aria-label="Remove">
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {(alwaysAsked || answered || hidden || field.isSensitive) && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
            {alwaysAsked && <ToneChip label="Always asked" tone="neutral" />}
            {answered && <ToneChip label="Has answers" tone="info" />}
            {field.isSensitive && <ToneChip label="Sensitive" tone="neutral" />}
            {hidden && <ToneChip label="Hidden from the form" tone="warning" />}
          </Stack>
        )}

        {takesOwnOptions(field) && (
          <Box sx={{ mt: 1.5 }}>
            <FieldOptionsInput options={field.options} onChange={options => set({ options })} />
          </Box>
        )}
      </Box>

      <Collapse in={more} unmountOnExit>
        <Box sx={(t) => ({ px: 2, py: 2, borderTop: `1px solid ${t.palette.divider}`, bgcolor: alpha(t.palette.text.primary, 0.015) })}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Help text" value={field.helpText ?? ''} onChange={e => set({ helpText: e.target.value || undefined })}
                size="small" fullWidth helperText="Shown under the question."
              />
              <TextField
                label="Placeholder" value={field.placeholder ?? ''} onChange={e => set({ placeholder: e.target.value || undefined })}
                size="small" fullWidth helperText="Shown inside the empty box."
              />
            </Stack>
            {limits && (
              <Stack direction="row" spacing={1.5}>
                {limits.map(k => (
                  <TextField
                    key={k} type="number" size="small" fullWidth
                    label={{ min: 'Lowest value', max: 'Highest value', minLength: 'Fewest characters', maxLength: 'Most characters' }[k]}
                    value={field.validation?.[k] ?? ''}
                    onChange={e => set({ validation: { ...field.validation, [k]: num(e.target.value) } })}
                  />
                ))}
              </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }}>
              {!alwaysAsked && (
                <FormControlLabel
                  control={<Switch size="small" checked={!hidden} onChange={(_, v) => set({ enabled: v })} />}
                  label={<Typography variant="body2">Show on the form</Typography>}
                  sx={{ ml: 0 }}
                />
              )}
              <FormControlLabel
                control={<Switch size="small" checked={!!field.isSensitive} onChange={(_, v) => set({ isSensitive: v })} />}
                label={<Typography variant="body2">Sensitive: only staff allowed to see sensitive details see the answer</Typography>}
                sx={{ ml: 0 }}
              />
            </Stack>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default React.memo(FieldCard);
